<script setup lang="ts">
	import { useCefFilterStore } from '@/stores/cefFilter'
	import { usePreviewCefStore } from '@/stores/previewCef'
	import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/20/solid'
	import { FunnelIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/24/outline'
	import { useDebounceFn } from '@vueuse/core'
	import { storeToRefs } from 'pinia'
	import axios from '@/lib/axios'
	import { computed, onMounted, provide, reactive, ref, watch } from 'vue'
	import { useRoute, useRouter } from 'vue-router'

	import BreadCrumb from '@/components/BreadCrumb.vue'
	import { Badge } from '@/components/ui/badge'
	import { Button } from '@/components/ui/button'
	import { Input } from '@/components/ui/input'
	import {
		Select,
		SelectContent,
		SelectItem,
		SelectTrigger,
		SelectValue,
	} from '@/components/ui/select'
	import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
	import { Loader2 } from 'lucide-vue-next'

	import useAuthUser from '@/composables/useAuthUser'
	import useState from '@/composables/useState'

	import { State } from '@/interfaces/state'

	import useCompaniesSearch from '@/modules/companies/composables/useCompaniesSearch'
	import useCompany from '@/modules/companies/composables/useCompany'
	import CefFilters from '@/modules/trips/components/CefFilters.vue'

	import CefListTable from '../components/CefListTable.vue'
	import PreviewRequest from '../components/PreviewRequest.vue'
	import useCefFilter from '../composables/useCefFilter'
	import { useCefRequests } from '../composables/useCefsRequests'
	import useCompanyCefs from '../composables/useCompanyCefs'
	import { Cef } from '../interfaces/cef'

	const currentRoute = useRoute()
	const router = useRouter()

	const { user } = useAuthUser()
	const { states } = useState()

	const selectedCef = ref<Cef>()
	const rawTab = currentRoute.query.tab
	const tabFromQuery = Array.isArray(rawTab) ? rawTab[0] : rawTab
	const activeTab = ref<string>(tabFromQuery || 'current')
	const isInitialLoad = ref(true)

	const { params } = useCefFilterStore()
	const { selectedCompany } = storeToRefs(useCefFilterStore())
	const { previewCef, clearPreviewCef } = usePreviewCefStore()

	const perPageOptions = [10, 15, 25, 50, 100]

	const tabs = [
		{
			id: 'current',
			name: 'En Curso',
			dotClass: 'bg-blue-500',
			activeClass:
				'data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-blue-200',
		},
		{
			id: 'past',
			name: 'Anteriores',
			dotClass: 'bg-slate-400',
			activeClass:
				'data-[state=active]:bg-slate-600 data-[state=active]:text-white data-[state=active]:shadow-slate-200',
		},
		{
			id: 'future',
			name: 'Próximos',
			dotClass: 'bg-emerald-500',
			activeClass:
				'data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-emerald-200',
		},
		{
			id: 'cancelled',
			name: 'Cancelados',
			dotClass: 'bg-red-500',
			activeClass:
				'data-[state=active]:bg-red-600 data-[state=active]:text-white data-[state=active]:shadow-red-200',
		},
		{
			id: 'solicitudes',
			name: 'Solicitudes',
			dotClass: 'bg-amber-500',
			activeClass:
				'data-[state=active]:bg-amber-600 data-[state=active]:text-white data-[state=active]:shadow-amber-200',
		},
	]

	const isReadyToFetch = computed(
		() => activeTab.value !== 'cancelled' || states.value.length > 0
	)

	const {
		data: cefs,
		page,
		pagination,
		isLoading,
		isRefetching,
		setPage,
		prefetch,
		searchParams,
		isFetching: isFetchingData,
	} = useCompanyCefs(params, isReadyToFetch)

	/**
	 * Buscador global: lo tecleado se compara contra número de CEF, nombre del
	 * itinerario, viajero, confirmación, proveedor y creador. Acepta varias
	 * palabras y las exige todas ("juan monterrey" = los viajes de Juan a
	 * Monterrey); el backend lo resuelve con ILIKE, así que no distingue
	 * mayúsculas.
	 *
	 * El término NO tumba los filtros del tab: cada pestaña busca dentro de lo
	 * suyo. Con "buscar en todas" encendido, además se piden los conteos de las
	 * otras pestañas para que se vea dónde más cayó el término.
	 */
	const nameSearch = ref((params.search as string) ?? '')

	/** Los filtros que le tocan a un tab. Un solo lugar para no desincronizarlos. */
	const tabFilters = (tab: string) => ({
		progressStage: tab === 'cancelled' || tab === 'solicitudes' ? null : tab,
		status:
			tab === 'cancelled' ?
				(states.value?.find((state: State) => state.displayName === 'Cancelado')
					?.id ?? null)
			:	null,
		onlyRequests: tab === 'solicitudes' ? true : null,
	})

	const debouncedNameSearch = useDebounceFn((val: string) => {
		const term = val.trim()
		params.search = term

		// La búsqueda siempre corre dentro de la pestaña: lo que cambia con la
		// casilla es si además se piden los conteos de las demás.
		Object.assign(params, tabFilters(activeTab.value))
		params.tabCounts = term && searchAllTabs.value ? true : null

		// Mientras hay término los números de las pestañas significan
		// "coincidencias", no "cuántos itinerarios tiene". Sin la casilla no se
		// buscó en las otras, así que se quedan sin número en vez de enseñar su
		// total de siempre al lado de un resultado filtrado.
		if (!term || !searchAllTabs.value) {
			Object.keys(tabCounts)
				// El de la pestaña activa se queda: el watch de `pagination` lo
				// reescribe enseguida con el total de la búsqueda.
				.filter((tab) => !term || tab !== activeTab.value)
				.forEach((tab) => delete tabCounts[tab])
		}

		params.page = 1
		page.value = 1
	}, 400)
	watch(nameSearch, (val) => debouncedNameSearch(val))

	const isSearching = computed(() => nameSearch.value.trim() !== '')

	/**
	 * Casilla junto al buscador: decide si el término se busca también en las
	 * otras pestañas. La lista siempre es la de la pestaña activa; lo que cambia
	 * es si se sabe cuántas coincidencias hay en las demás.
	 *
	 * Apagada por default: quien busca casi siempre busca en la pestaña donde ya
	 * está, y así no se cargan los conteos de las otras cuatro sin que nadie los
	 * haya pedido. Prenderla es un click.
	 */
	const searchAllTabs = ref(false)

	watch(searchAllTabs, () => {
		if (!isSearching.value) return

		params.tabCounts = searchAllTabs.value ? true : null

		// Apagada, los conteos de las otras pestañas dejan de llegar: se borran en
		// vez de dejarlos congelados diciendo un número que ya nadie recalcula.
		if (!searchAllTabs.value) {
			Object.keys(tabCounts)
				.filter((tab) => tab !== activeTab.value)
				.forEach((tab) => delete tabCounts[tab])
		}
	})

	const activeTabName = computed(
		() => tabs.find((t) => t.id === activeTab.value)?.name ?? ''
	)

	/**
	 * Los filtros del panel avanzado, vacíos. Es un objeto y no seis asignaciones
	 * sueltas porque el prefetch de las otras pestañas tiene que armar la MISMA
	 * combinación que quedará al cambiar de tab; si no coincide, la llave de
	 * caché no coincide y la petición se repite.
	 */
	const EMPTY_SPECIFIC_FILTERS = {
		cef: undefined,
		confirmation: '',
		name: '',
		travelerName: '',
		creatorName: '',
		supplierName: '',
		tripReason: '',
	}

	const clearSpecificFilters = ({ keepSearch = false } = {}) => {
		Object.assign(params, EMPTY_SPECIFIC_FILTERS)

		if (!keepSearch) {
			params.search = ''
			nameSearch.value = ''
		}
	}

	const changeTab = (newTab: string) => {
		if (activeTab.value === newTab || isFetchingData.value) return
		isInitialLoad.value = false
		activeTab.value = newTab
		router.replace({ query: { ...currentRoute.query, tab: newTab } })
	}

	watch(
		activeTab,
		(newTab) => {
			// Los filtros del tab se aplican siempre, haya búsqueda o no: el término
			// se suma a ellos, no los reemplaza.
			if (newTab === 'cancelled') {
				params.status = states.value.find(
					(state) => state.displayName === 'Cancelado'
				)?.id
				params.progressStage = null
				params.onlyRequests = null
			} else if (newTab === 'solicitudes') {
				// Solicitudes de hotel offline pendientes de cotizar: sin etapa/estado,
				// solo el filtro dedicado del backend.
				params.status = null
				params.progressStage = null
				params.onlyRequests = true
			} else {
				params.status = null
				params.progressStage = newTab
				params.onlyRequests = null
			}
			// La búsqueda sobrevive al cambio de pestaña: sólo se limpian los
			// filtros del panel avanzado.
			if (!isInitialLoad.value) clearSpecificFilters({ keepSearch: true })
			params.page = 1
			page.value = 1
		},
		{ immediate: true }
	)

	const tabCounts = reactive<Record<string, number>>({})

	// El total de la consulta es el de la pestaña activa, así que puede escribirse
	// directo: ya viene acotado por el tab, con o sin búsqueda.
	watch(pagination, (p) => {
		if (p?.meta?.total != null) tabCounts[activeTab.value] = p.meta.total
	})

	/**
	 * Deja cargadas las pestañas donde el término sí cayó, para que moverse entre
	 * ellas no vuelva a pegarle al backend: la búsqueda ya se hizo.
	 *
	 * Sólo las que tienen coincidencias: pedir una lista que se sabe vacía sería
	 * gastar una petición para pintar "sin resultados".
	 */
	const prefetchTabsWithMatches = () => {
		if (!isSearching.value || !searchAllTabs.value) return

		tabs
			.filter((tab) => tab.id !== activeTab.value && tabCounts[tab.id] > 0)
			.forEach((tab) =>
				prefetch({ ...EMPTY_SPECIFIC_FILTERS, ...tabFilters(tab.id) })
			)
	}

	// El desglose de las otras pestañas sólo llega con la casilla encendida.
	watch(
		() => pagination.value?.tabCounts,
		(counts) => {
			if (!counts) return

			Object.assign(tabCounts, counts)
			prefetchTabsWithMatches()
		},
		{ deep: true }
	)

	/**
	 * Los conteos por pestaña ya vienen en la respuesta del listado
	 * (`tab_counts`, ver CompanyCefController): con búsqueda activa el backend los
	 * calcula en la misma consulta. Antes se pedían con cinco peticiones desde
	 * aquí, que era repetir un trabajo que el servidor ya podía hacer de un viaje.
	 */

	const handlePage = (newPage: string | number) => {
		const pageNumber = Number(newPage)
		if (pageNumber >= 1 && pageNumber <= pagination.value?.meta.lastPage!) {
			// La página va en la llave de la consulta: cambiarla ya dispara la
			// petición (o sirve de caché si esa página ya se vio).
			setPage(pageNumber)
		}
	}

	const throttledHandlePage = (newPage: string | number) => {
		if (!isFetchingData.value) handlePage(newPage)
	}

	// Sin watch que refetchee a mano: la llave de la consulta lleva todos los
	// filtros, así que vue-query pide sola cuando cambian y responde de caché
	// cuando se vuelve a una combinación ya vista.
	const { activeFilters } = useCefFilter(params)

	const { companies, setQuery, refetch } = useCompaniesSearch({
		immediate: true,
		queryKey: ['companies'],
	})

	const { company } = useCompany(
		{ companyId: user.value?.companyId },
		{ immediate: false }
	)

	const {
		cefId,
		refetch: refetchRequets,
		isFetching,
	} = useCefRequests({
		params: { cefId: selectedCef.value?.id },
		immediate: false,
	})

	const resetFilters = () => {
		params.company = user.value?.companyId
		params.search = ''
		nameSearch.value = ''
		params.travelerName = ''
		params.cef = ''
		params.confirmation = ''
		params.name = ''
		company.value = companies.value[0]
		params.travelerName = ''
		// Mismo helper que el buscador: si se duplica la lógica, un día divergen.
		Object.assign(params, tabFilters(activeTab.value))
		params.order = 'desc'
		params.cef = undefined
		params.confirmation = ''
		company.value = undefined
		params.creatorName = ''
		params.channel = undefined
		params.creationDates = []
		params.dates = []
		params.folio = undefined
		params.hasLetter = undefined
		params.service = undefined
		params.supplierName = ''
		params.tripReason = ''
		params.perPage = 15
	}

	const pages = [{ name: 'Viajes', to: '/trips', current: false }]
	const openSliderFilter = ref(false)
	const toggleSliderFilter = () => (openSliderFilter.value = false)

	provide('cef', { searchParams })

	const handlePreviewRequest = (cef: Cef) => (selectedCef.value = cef)
	const closePreviewRequest = () => (selectedCef.value = undefined)

	onMounted(async () => {
		if (previewCef) {
			params.cef = String(previewCef.cefId)
			activeTab.value = previewCef.cefProgressStage
			await searchParams()
			isInitialLoad.value = false
			clearPreviewCef()
		} else {
			isInitialLoad.value = false
		}
	})

	watch(
		() => [cefs.value, previewCef],
		() => {
			if (previewCef && cefs.value?.data) {
				const foundCef = cefs.value.data.find(
					(cef) => cef.id === previewCef.cefId
				)
				if (foundCef) {
					selectedCef.value = foundCef
					cefId.value = foundCef.id
					refetchRequets()
					clearPreviewCef()
				}
			}
		},
		{ once: previewCef !== undefined }
	)
</script>

<template>
	<BreadCrumb :pages="pages" />
	<main class="flex justify-center items-start mt-4 px-4 sm:px-8 w-full">
		<div class="flex flex-col lg:flex-row w-full gap-4">
			<!-- Left panel -->
			<div
				class="flex flex-col gap-3 w-full min-w-0 transition-all duration-300"
				:class="[selectedCef ? 'lg:flex-1' : '']"
			>
				<!-- Header card -->
				<div
					class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
				>
					<!-- Top bar: title + filter button -->
					<div
						class="px-6 pt-6 pb-4 flex flex-col sm:flex-row sm:items-start justify-between gap-4"
					>
						<div>
							<h1 class="font-bold text-2xl text-gray-900 tracking-tight">
								Gestión de Itinerarios
							</h1>
							<p class="text-sm text-gray-500 mt-0.5">
								<transition
									enter-active-class="transition-all duration-300"
									enter-from-class="opacity-0 translate-y-1"
									enter-to-class="opacity-100 translate-y-0"
									mode="out-in"
								>
									<span :key="selectedCompany?.name ?? 'all'">
										{{
											selectedCompany ?
												selectedCompany.name
											:	'Administra y supervisa todos los itinerarios'
										}}
									</span>
								</transition>
							</p>
						</div>

						<Button
							variant="outline"
							class="shrink-0 gap-2 relative"
							@click="openSliderFilter = !openSliderFilter"
						>
							<FunnelIcon class="w-4 h-4" />
							<span>Filtros</span>
							<transition
								enter-active-class="transition-all duration-200 ease-out"
								enter-from-class="opacity-0 scale-75"
								enter-to-class="opacity-100 scale-100"
								leave-active-class="transition-all duration-150 ease-in"
								leave-from-class="opacity-100 scale-100"
								leave-to-class="opacity-0 scale-75"
							>
								<Badge
									v-if="activeFilters.length > 0"
									class="absolute -top-2 -right-2 w-5 h-5 p-0 flex items-center justify-center text-[10px] rounded-full bg-blue-600 text-white border-2 border-white"
								>
									{{ activeFilters.length }}
								</Badge>
							</transition>
						</Button>
					</div>

					<!-- Search -->
					<div class="px-6 pb-4">
						<div class="relative">
							<MagnifyingGlassIcon
								class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
							/>
							<Input
								v-model="nameSearch"
								placeholder="CEF, itinerario, viajero, hotel, confirmación… (varias palabras)"
								class="pl-9 pr-8 h-9 bg-gray-50 border-gray-200 rounded-xl text-sm"
							/>
							<button
								v-if="nameSearch"
								class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
								@click="nameSearch = ''"
							>
								<XMarkIcon class="w-3.5 h-3.5" />
							</button>
						</div>

						<!-- Opción visible en vez de un modo automático: la búsqueda vive
						     dentro de la pestaña, y esto la extiende a las demás. -->
						<label
							class="mt-2 flex w-fit cursor-pointer items-center gap-2 text-xs text-gray-600"
						>
							<input
								v-model="searchAllTabs"
								type="checkbox"
								class="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 accent-blue-600"
							/>
							Buscar en todas las pestañas
						</label>
					</div>

					<!-- Sin esto, ver un itinerario de "Anteriores" mientras el tab dice
					     "En Curso" parece un bug en vez de la función. -->
					<div
						v-if="isSearching"
						class="px-6 pb-3 -mt-1"
					>
						<p
							v-if="searchAllTabs"
							class="text-xs text-gray-500"
						>
							Buscando en <strong>todas las pestañas</strong>: el número de cada
							una dice cuántas coincidencias tiene. Aquí se ven las de
							<strong>{{ activeTabName }}</strong
							>.
						</p>
						<p
							v-else
							class="text-xs text-gray-500"
						>
							Buscando sólo dentro de <strong>{{ activeTabName }}</strong
							>.
						</p>
					</div>

					<!-- Tabs -->
					<div class="px-6 pb-0">
						<Tabs
							:model-value="activeTab"
							@update:model-value="(v) => changeTab(String(v))"
						>
							<TabsList
								class="bg-gray-100/70 p-1 gap-0.5 h-auto rounded-xl w-full sm:w-auto"
							>
								<TabsTrigger
									v-for="tab in tabs"
									:key="tab.id"
									:value="tab.id"
									:class="[
										tab.activeClass,
										'text-xs font-semibold px-3 py-1.5 rounded-lg transition-all duration-200 data-[state=active]:shadow-md',
										isFetchingData && activeTab !== tab.id
											? 'opacity-50 cursor-not-allowed pointer-events-none'
											: '',
									]"
								>
									<span class="flex items-center gap-1.5">
										<Loader2
											v-if="isFetchingData && activeTab === tab.id"
											class="w-3 h-3 animate-spin shrink-0 text-white/80"
										/>
										<span
											v-else
											class="w-1.5 h-1.5 rounded-full shrink-0 transition-all duration-200"
											:class="[activeTab === tab.id ? 'bg-white/80' : tab.dotClass]"
										/>
										{{ tab.name }}
										<span
											v-if="tabCounts[tab.id] != null"
											class="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold leading-none"
											:class="activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-500/15 text-slate-500'"
										>
											{{ tabCounts[tab.id] }}
										</span>
									</span>
								</TabsTrigger>
							</TabsList>
						</Tabs>

						<!-- Active CEF filter indicator -->
						<transition
							enter-active-class="transition-all duration-300 ease-out"
							enter-from-class="opacity-0 -translate-y-2 max-h-0"
							enter-to-class="opacity-100 translate-y-0 max-h-16"
							leave-active-class="transition-all duration-200 ease-in"
							leave-from-class="opacity-100 translate-y-0 max-h-16"
							leave-to-class="opacity-0 -translate-y-2 max-h-0"
						>
							<div
								v-if="activeFilters.find((f) => f.reference === 'cef')"
								class="mt-3 mb-1 flex items-center gap-2 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-fit"
							>
								<FunnelIcon class="w-3.5 h-3.5 text-amber-500 shrink-0" />
								<span class="text-amber-500">Filtrado por:</span>
								<span class="font-bold">
									{{ activeFilters.find((f) => f.reference === 'cef')?.value }}
								</span>
								<button
									@click="params.cef = undefined"
									class="ml-1 p-0.5 rounded-full hover:bg-amber-100 text-amber-500 hover:text-amber-700 transition-colors"
								>
									<XMarkIcon class="w-3 h-3" />
								</button>
							</div>
						</transition>
					</div>

					<div class="h-4" />
				</div>

				<!-- Skeleton — initial load only -->
				<transition
					enter-active-class="transition-opacity duration-300"
					enter-from-class="opacity-0"
					enter-to-class="opacity-100"
					leave-active-class="transition-opacity duration-200"
					leave-from-class="opacity-100"
					leave-to-class="opacity-0"
				>
					<div
						v-if="isLoading"
						class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
					>
						<div class="divide-y divide-gray-50">
							<div
								v-for="i in (params.perPage ?? 15) > 10 ?
									10
								:	(params.perPage ?? 15)"
								:key="i"
								class="flex items-center gap-4 px-6 py-4 animate-pulse"
							>
								<div class="w-8 h-8 bg-gray-100 rounded-lg shrink-0" />
								<div class="flex-1 space-y-2">
									<div class="h-3 bg-gray-100 rounded w-1/3" />
									<div class="h-2.5 bg-gray-50 rounded w-1/2" />
								</div>
								<div class="hidden sm:flex gap-6">
									<div class="h-3 bg-gray-100 rounded w-20" />
									<div class="h-3 bg-gray-100 rounded w-16" />
									<div class="h-5 bg-gray-100 rounded-full w-20" />
								</div>
							</div>
						</div>
					</div>
				</transition>

				<!-- Results area -->
				<template v-if="cefs && !isLoading">
					<!-- Status bar -->
					<transition
						enter-active-class="transition-all duration-300 ease-out"
						enter-from-class="opacity-0 -translate-y-1"
						enter-to-class="opacity-100 translate-y-0"
					>
						<div
							v-if="pagination && cefs.data.length > 0"
							class="flex items-center justify-between gap-3 px-4 py-2.5 bg-white rounded-xl border border-gray-100 shadow-sm text-sm"
						>
							<div class="flex items-center gap-2.5 text-gray-600">
								<div
									:class="[
										'w-2 h-2 rounded-full transition-colors duration-300',
										isFetchingData || isRefetching ?
											'bg-amber-400 animate-pulse'
										:	'bg-emerald-500',
									]"
								/>
								<span>
									Mostrando
									<span class="font-semibold text-gray-900 tabular-nums">{{
										pagination.meta.from
									}}</span>
									–
									<span class="font-semibold text-gray-900 tabular-nums">{{
										pagination.meta.to
									}}</span>
									de
									<span class="font-semibold text-blue-600 tabular-nums">{{
										pagination.meta.total
									}}</span>
									itinerarios
								</span>
							</div>
							<div class="flex items-center gap-2 text-xs text-gray-400">
								<transition
									enter-active-class="transition-all duration-200"
									enter-from-class="opacity-0"
									enter-to-class="opacity-100"
									leave-active-class="transition-all duration-150"
									leave-from-class="opacity-100"
									leave-to-class="opacity-0"
									mode="out-in"
								>
									<span :key="isFetchingData ? 'loading' : 'ready'">
										{{
											isFetchingData ? 'Actualizando...'
											: isRefetching ? 'Refrescando...'
											: `Pág. ${pagination.meta.currentPage} / ${pagination.meta.lastPage}`
										}}
									</span>
								</transition>
							</div>
						</div>
					</transition>

					<!-- Table container -->
					<div
						class="relative bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
					>
						<!-- Loading overlay -->
						<transition
							enter-active-class="transition-opacity duration-250 ease-out"
							enter-from-class="opacity-0"
							enter-to-class="opacity-100"
							leave-active-class="transition-opacity duration-200 ease-in"
							leave-from-class="opacity-100"
							leave-to-class="opacity-0"
						>
							<div
								v-if="isFetchingData && !isLoading"
								class="absolute inset-0 bg-white/80 backdrop-blur-[2px] z-20 flex items-center justify-center"
							>
								<div
									class="flex items-center gap-3 bg-white px-5 py-3 rounded-xl shadow-md border border-gray-100"
								>
									<div
										class="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"
									/>
									<span class="text-sm font-medium text-gray-700"
										>Cargando...</span
									>
								</div>
							</div>
						</transition>

						<CefListTable
							:cefs="cefs"
							:selected-cef="selectedCef"
							:is-fetching="isFetchingData"
							:is-loading="isLoading"
							:is-refetching="isRefetching"
							@select-cef="handlePreviewRequest"
							@close-preview-request="closePreviewRequest"
							@refetch="searchParams"
						/>
					</div>

					<!-- Pagination -->
					<div
						v-if="pagination"
						class="flex flex-wrap items-center justify-between gap-3 pt-2"
					>
						<div class="flex items-center gap-2">
							<span class="text-xs text-gray-400 hidden sm:block">
								{{ pagination.meta.total }} registros
							</span>
							<span class="text-xs text-gray-300 hidden sm:block">·</span>
							<div class="flex items-center gap-1.5">
								<span class="text-xs text-gray-400 whitespace-nowrap"
									>Por página</span
								>
								<Select
									:model-value="String(params.perPage ?? 15)"
									@update:model-value="
										(v) => {
											params.perPage = Number(v)
											setPage(1)
										}
									"
								>
									<SelectTrigger
										class="h-7 w-16 text-xs border-gray-200 bg-white rounded-lg"
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem
											v-for="opt in perPageOptions"
											:key="opt"
											:value="String(opt)"
											class="text-xs"
										>
											{{ opt }}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>

						<nav
							v-if="pagination.meta.lastPage > 1"
							class="flex items-center gap-1 mx-auto sm:mx-0"
						>
							<button
								:disabled="pagination.meta.currentPage === 1 || isFetchingData"
								:class="[
									'flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
									pagination.meta.currentPage === 1 || isFetchingData ?
										'text-gray-300 cursor-not-allowed'
									:	'text-gray-600 hover:text-blue-600 hover:bg-blue-50',
								]"
								@click="throttledHandlePage(+page - 1)"
							>
								<ChevronLeftIcon class="w-4 h-4" />
								<span class="hidden sm:inline">Anterior</span>
							</button>

							<div class="flex items-center gap-1">
								<template
									v-for="link in pagination.meta?.links?.filter((link: any) =>
										link.label.match(/\d/)
									)"
									:key="link.label"
								>
									<button
										:disabled="isFetchingData"
										:class="[
											'w-9 h-9 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center',
											link.active ?
												'bg-blue-600 text-white shadow-sm shadow-blue-200'
											:	'text-gray-600 hover:text-blue-600 hover:bg-blue-50',
											isFetchingData && 'opacity-40 cursor-not-allowed',
										]"
										@click="throttledHandlePage(link.label)"
									>
										<span v-if="!isFetchingData || !link.active">{{
											link.label
										}}</span>
										<div
											v-else
											class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"
										/>
									</button>
								</template>
							</div>

							<button
								:disabled="
									pagination.meta.currentPage === pagination.meta.lastPage ||
									isFetchingData
								"
								:class="[
									'flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
									(
										pagination.meta.currentPage === pagination.meta.lastPage ||
										isFetchingData
									) ?
										'text-gray-300 cursor-not-allowed'
									:	'text-gray-600 hover:text-blue-600 hover:bg-blue-50',
								]"
								@click="throttledHandlePage(+page + 1)"
							>
								<span class="hidden sm:inline">Siguiente</span>
								<ChevronRightIcon class="w-4 h-4" />
							</button>
						</nav>
					</div>
				</template>
			</div>

			<!-- Preview panel -->
			<transition
				enter-active-class="transition-opacity duration-200 ease-out"
				enter-from-class="opacity-0"
				enter-to-class="opacity-100"
				leave-active-class="transition-opacity duration-150 ease-in"
				leave-from-class="opacity-100"
				leave-to-class="opacity-0"
			>
				<div
					v-if="selectedCef"
					class="fixed inset-0 z-30 flex items-center justify-center p-4 lg:static lg:inset-auto lg:z-10 lg:flex lg:items-start lg:p-0 lg:w-2/5 lg:shrink-0"
				>
					<div
						class="absolute inset-0 bg-black/40 backdrop-blur-sm lg:hidden"
						@click="closePreviewRequest"
					/>
					<div class="relative w-full max-w-lg lg:max-w-none overflow-y-auto max-h-[90vh] lg:max-h-[80vh] lg:sticky lg:top-4">
						<PreviewRequest
							:key="selectedCef.id"
							:cef="selectedCef"
							:isFetching="isFetching"
							:active-tab="activeTab"
							@close-preview-request="closePreviewRequest"
						/>
					</div>
				</div>
			</transition>
		</div>
	</main>

	<CefFilters
		:active-tab="activeTab"
		:states="states"
		:companies="companies"
		v-model:company="params.company"
		v-model:traveler-name="params.travelerName"
		v-model:order="params.order"
		v-model:state="params.active"
		v-model:cef="params.cef"
		v-model:name="params.name"
		v-model:status="params.status"
		v-model:progressStage="params.progressStage"
		v-model:confirmation="params.confirmation"
		v-model:creator-name="params.creatorName"
		v-model:channel="params.channel"
		v-model:creation-dates="params.creationDates"
		v-model:dates="params.dates"
		v-model:folio="params.folio"
		v-model:has-letter="params.hasLetter"
		v-model:service="params.service"
		v-model:supplier-name="params.supplierName"
		v-model:trip-reason="params.tripReason"
		@reset-filters="resetFilters"
		@search="setQuery"
		:open-slide-over="openSliderFilter"
		@toggleFilter="toggleSliderFilter"
		@refetchCompanies="refetch"
	/>
</template>
